import type { KeyboardEvent } from 'react';
import type { Vendor, Category } from '../../../types';
import { CategoryGlyph } from '../../../components/primitives/CategoryGlyph';
import { ChevRightIcon } from '../../../components/primitives/Icon';
import { legibleOn } from '../../../utils/legibleOn';
import { isAmenityType, amenityFill } from '../../../utils/amenities';
import { DEFAULT_CATEGORY_COLOR } from '../../../utils/categoryColors';
import { resolveGlyphKey } from '../../../utils/glyphKey';
import { LocationTag } from './parts/LocationTag';
import styles from './VendorRow.module.css';

interface Props {
  vendor: Vendor;
  category?: Category | undefined;
  locationName?: string | undefined;
  isSelected?: boolean;
  onTap: (vendor: Vendor) => void;
}

export function VendorRow({ vendor, category, locationName, isSelected, onTap }: Props) {
  const color = isAmenityType(vendor.type)
    ? amenityFill(vendor.type)
    : (category?.color ?? DEFAULT_CATEGORY_COLOR);
  const dataCatId = isAmenityType(vendor.type) ? vendor.type : vendor.categoryId;
  const glyphId = resolveGlyphKey(vendor.type, vendor.categoryId);

  // Native <button> already handles Enter/Space activation without
  // scrolling the page, but the explicit onKeyDown gives us deterministic
  // behavior under the test runner (jsdom doesn't fully simulate the UA's
  // built-in click-on-Space), and lets us call preventDefault on Space so
  // any ancestor scroll handler doesn't fire either.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      onTap(vendor);
    }
  };

  return (
    <button
      type="button"
      className={`${styles.row}${isSelected ? ` ${styles.rowSelected}` : ''}`}
      onClick={() => onTap(vendor)}
      onKeyDown={handleKeyDown}
      aria-label={`Open ${vendor.name}`}
      data-row-uid={vendor.uid}
    >
      <div className={styles.avatar} data-cat-id={dataCatId}>
        <CategoryGlyph categoryId={glyphId} size={40} color={legibleOn(color)} bgColor={color} />
      </div>
      <div className={styles.body}>
        <div className={styles.titleLine}>
          <span className={styles.name}>{vendor.name}</span>
        </div>
        {vendor.description && <div className={styles.desc}>{vendor.description}</div>}
        {locationName && (
          <div className={styles.metaLine}>
            <LocationTag street={locationName} size="sm" />
          </div>
        )}
      </div>
      <ChevRightIcon size={14} color="var(--color-text-muted)" className={styles.chev} />
    </button>
  );
}
